/**
 * A project's History pane (RFC 0313 3i, canvas 5).
 *
 * Opened from the project's row. Document changes come from each document's
 * own change graph, read only for documents the catalog says moved recently,
 * so a quiet document costs nothing. Sessions come from the registry, which
 * needs no hydration. Every row opens what it names.
 */

import type {
  AnyDocumentId,
  AnyDocumentId as DocId,
  Repo,
} from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  createBranch,
  type HistoryChange,
  projectAuthor,
  projectChanges,
  projectDocuments,
  projectSessions,
  projectSources,
  projectString,
  readVersions,
  saveVersion,
} from '@mod/core/model'
import { ChevronRight, FileText, GitBranch } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { setProjectBranch } from '../../services/branches/branchSelection'
import { CommitRow, FileRows, LineCounts } from '../../services/code/CommitRow'
import {
  type CodeFileChange,
  lineCounts,
  useRecentCommits,
  useWorkingFiles,
} from '../../services/code/codeHistory'
import { readLocalPerson } from '../../services/identity/localPerson'
import { SessionMark } from '../../services/sessions/SessionMark'
import {
  useDocument,
  useDocuments,
} from '../../services/world/repo/useDocument'
import {
  buildTimeline,
  recentDocuments,
  type TimelineFilter,
  type TimelineRow,
} from './timeline'

const FILTERS: { id: TimelineFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'documents', label: 'Documents' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'code', label: 'Code' },
  { id: 'versions', label: 'Versions' },
]

function dayLabel(at: number, now: number): string {
  const startOf = (t: number) => {
    const d = new Date(t)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  }
  const days = Math.round((startOf(now) - startOf(at)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return new Date(at).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  })
}

const time = (at: number) =>
  new Date(at).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })

/** The checkout's uncommitted files, one row that opens to list them. */
function WorkingRow({
  files,
  onOpen,
  sourceId,
}: {
  files: CodeFileChange[]
  onOpen?: (codeId: string) => void
  sourceId?: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-border/50 border-b px-4 py-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 text-left"
      >
        <GitBranch className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] text-foreground">
            Not committed
          </span>
          <span className="block text-muted-foreground text-xs">
            {files.length} file{files.length === 1 ? '' : 's'} changed in the
            project folder · <LineCounts {...lineCounts(files)} />
          </span>
        </span>
        <ChevronRight
          className={`mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform ${
            open ? 'rotate-90' : ''
          }`}
        />
      </button>
      {open ? (
        <div className="pl-5">
          <FileRows
            files={files}
            at={null}
            sourceId={sourceId}
            {...(onOpen ? { onOpen } : {})}
          />
        </div>
      ) : null}
    </div>
  )
}

export function ProjectHistoryView({
  projectId,
  onOpenDocument,
  onOpenSession,
}: {
  projectId: string
  onOpenDocument?: (id: string) => void
  onOpenSession?: (id: string) => void
}) {
  const repo = useRepo() as unknown as Repo
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const catalog = projectSources(projectDoc)
  const [sourceId, setSourceId] = useState(catalog.defaultSourceId)
  useEffect(() => {
    if (!catalog.sources.some((s) => s.id === sourceId))
      setSourceId(catalog.defaultSourceId)
  }, [catalog, sourceId])
  const selectedSource = catalog.sources.find((s) => s.id === sourceId)
  const versionsId = projectString(
    (projectDoc as { versionsDocId?: unknown } | undefined)?.versionsDocId,
  )
  const [versionsDoc] = useDocument<unknown>(
    (versionsId ?? null) as DocId | null,
  )
  const versions = useMemo(() => readVersions(versionsDoc), [versionsDoc])
  const [saving, setSaving] = useState(false)
  const [naming, setNaming] = useState<string | null>(null)
  const [filter, setFilter] = useState<TimelineFilter>('all')
  // Read once per mount: the window is "the last two weeks from when you
  // opened it", and a clock that moved every render would re-open documents.
  const [now] = useState(() => Date.now())
  const recent = useMemo(
    () => recentDocuments(projectDocuments(projectDoc), now),
    [projectDoc, now],
  )
  const docs = useDocuments<unknown>(recent.map((d) => d.id as AnyDocumentId))
  const rows = useMemo(() => {
    const m = new Map<string, HistoryChange[]>()
    for (const d of recent) {
      const doc = docs.get(d.id)
      if (doc) m.set(d.id, projectChanges(doc, { limit: 50 }).changes)
    }
    return m
  }, [recent, docs])
  // The code half (3n), asked of this device's git server; [] for a project
  // with no checkout here, so a docs-only project draws no Code rows.
  const codeProject = selectedSource?.kind === 'git' ? projectId : undefined
  const commits = useRecentCommits(codeProject, 14, sourceId)
  const working = useWorkingFiles(codeProject, sourceId)
  const timeline = useMemo(
    () =>
      buildTimeline({
        documents: recent,
        rows,
        sessions: projectSessions(projectDoc),
        versions,
        commits,
        working,
        now,
        filter,
      }),
    [recent, rows, projectDoc, versions, commits, working, filter, now],
  )

  const name = (who: string) => {
    if (who === 'none') return 'Unattributed'
    const [kind, id] = [who.slice(0, 1), who.slice(2)]
    return projectAuthor(projectDoc, {
      author: kind === 'p' ? id : '',
      ...(kind === 's' ? { authorSessionId: id } : {}),
    }).label
  }

  let lastDay = ''
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <div className="flex gap-1.5 px-4 pt-3 pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
            className={`rounded-full border px-2.5 py-0.5 text-xs ${
              filter === f.id
                ? 'border-foreground bg-foreground text-background'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
        {catalog.sources.length > 1 && (
          <select
            aria-label="History source"
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
            className="rounded-full border border-border bg-background px-2 text-xs"
          >
            {catalog.sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
        )}
        <span className="flex-1" />
        {naming === null ? (
          <button
            type="button"
            disabled={saving}
            onClick={() => setNaming('')}
            className="rounded-full border border-border px-2.5 py-0.5 text-muted-foreground text-xs hover:text-foreground disabled:opacity-50"
          >
            Save version
          </button>
        ) : (
          // Inline, not window.prompt: Electron has no prompt dialog.
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const name = naming.trim()
              if (!name) return
              setSaving(true)
              void saveVersion(repo, projectId, {
                name,
                by: readLocalPerson()?.id ?? 'user',
                covers: { kind: 'project' },
              }).finally(() => {
                setSaving(false)
                setNaming(null)
              })
            }}
          >
            <input
              // biome-ignore lint/a11y/noAutofocus: they asked to name it
              autoFocus
              value={naming}
              onChange={(e) => setNaming(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setNaming(null)}
              placeholder="Name this version"
              className="rounded-md border border-border bg-transparent px-2 py-0.5 text-xs outline-none"
            />
          </form>
        )}
      </div>
      {timeline.length === 0 ? (
        <p className="px-4 py-3 text-muted-foreground text-xs">
          Nothing in the last two weeks.
        </p>
      ) : null}
      {timeline.map((row: TimelineRow) => {
        const day = dayLabel(row.at, now)
        const heading = day !== lastDay ? day : null
        lastDay = day
        return (
          <div key={row.key}>
            {heading ? (
              <h3 className="px-4 pt-3 pb-1 text-[11px] text-muted-foreground">
                {heading}
              </h3>
            ) : null}
            {row.kind === 'working' ? (
              <WorkingRow
                files={row.files}
                sourceId={sourceId}
                {...(onOpenDocument ? { onOpen: onOpenDocument } : {})}
              />
            ) : row.kind === 'commit' ? (
              <div className="border-border/50 border-b px-4 py-2">
                <CommitRow
                  projectId={projectId}
                  sourceId={sourceId}
                  commit={row.commit}
                  byline={
                    <>
                      {row.commit.branch ? `${row.commit.branch} · ` : ''}
                      {row.commit.author} · {time(row.at)}
                    </>
                  }
                  {...(onOpenDocument ? { onOpen: onOpenDocument } : {})}
                />
              </div>
            ) : row.kind === 'version' ? (
              <div className="mx-3 my-1 flex items-start gap-2.5 rounded-lg border border-border border-dashed px-3 py-2">
                <span className="text-muted-foreground text-xs">⚑</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-foreground">
                    {row.version.name}
                  </span>
                  <span className="block text-muted-foreground text-xs">
                    Version ·{' '}
                    {row.version.by === 'auto' ? 'saved by Mod' : 'saved'} ·{' '}
                    {row.documents} document{row.documents === 1 ? '' : 's'}
                  </span>
                </span>
                {/* RFC 0313 3c: a project version opens as a branch, never
                    restored in place, because people are working in main. */}
                <button
                  type="button"
                  onClick={() => {
                    const name = `from-${row.version.name}`
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '-')
                      .slice(0, 40)
                    void createBranch(repo, projectId, {
                      name,
                      by: readLocalPerson()?.id ?? 'user',
                      fromVersion: row.version.id,
                    })
                      .then(() => setProjectBranch(projectId, name))
                      .catch(() => {})
                  }}
                  className="shrink-0 rounded-full border border-border px-2 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Branch from here
                </button>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {time(row.at)}
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() =>
                  row.kind === 'document'
                    ? onOpenDocument?.(row.documentId)
                    : onOpenSession?.(row.sessionId)
                }
                className="flex w-full items-start gap-2.5 border-border/50 border-b px-4 py-2 text-left hover:bg-muted/50"
              >
                {row.kind === 'document' ? (
                  <FileText className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <SessionMark id={row.sessionId} size={14} />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-foreground">
                    {row.kind === 'document'
                      ? row.title
                      : `${row.title} ${row.finished ? 'finished' : 'was active'}`}
                  </span>
                  {row.kind === 'document' ? (
                    <span className="block truncate text-muted-foreground text-xs">
                      {row.who.map(name).join(' and ')}, {row.changes} change
                      {row.changes === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {time(row.at)}
                </span>
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
